import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

interface GroupModalProps {
  showOptionModal: boolean;
  onCancel: () => void;
  onSubmit: (options: string[], columnInfo: any) => void;
  group?: string[];
  columnInfo?: any;
  fields?: any[];
}

const GroupModal: React.FC<GroupModalProps> = ({
  showOptionModal,
  onCancel,
  onSubmit,
  group = [],
  columnInfo = {},
  fields = [],
}) => {
  const [options, setOptions] = useState<Array<{ title: string; fieldKeys: string[] }>>([
    { title: '', fieldKeys: [] },
  ]);

  useEffect(() => {
    if (Array.isArray(group) && group.length > 0) {
      const savedGroups = group.map((title) => ({
        title: typeof title === 'string' ? title : '',
        fieldKeys: Array.isArray(columnInfo?.groupFieldMap?.[title])
          ? columnInfo.groupFieldMap[title]
          : [],
      }));
      setOptions(savedGroups.length ? savedGroups : [{ title: '', fieldKeys: [] }]);
    } else {
      setOptions([{ title: '', fieldKeys: [] }]);
    }
  }, [group, columnInfo]);

  const handleInputChange = (value: string, index: number) => {
    const updated = [...options];
    updated[index] = { ...updated[index], title: value };
    setOptions(updated);
  };

  const handleFieldChange = (value: string[], index: number) => {
    const updated = [...options];
    updated[index] = { ...updated[index], fieldKeys: value };
    setOptions(updated);
  };

  const handleRemoveOption = (index: number) => {
    const updated = [...options];
    updated.splice(index, 1);
    setOptions(updated.length ? updated : [{ title: '', fieldKeys: [] }]);
  };

  const addOptionRow = () => {
    setOptions((prev) => [...prev, { title: '', fieldKeys: [] }]);
  };

  const handleSubmit = () => {
    const cleaned = options
      .map((opt) => (typeof opt?.title === 'string' ? opt.title.trim() : ''))
      .filter((opt) => opt !== '');

    const unique = Array.from(new Set(cleaned));
    const removedGroups = (group || [])
      .map((g) => (typeof g === 'string' ? g.trim() : ''))
      .filter((orig) => orig && !unique.includes(orig));

    const groupFieldMap = options.reduce((acc, opt) => {
      if (!opt?.title?.trim()) return acc;
      acc[opt.title.trim()] = Array.isArray(opt.fieldKeys) ? opt.fieldKeys : [];
      return acc;
    }, {});

    onSubmit(unique, { ...columnInfo, removedGroups, groupFieldMap });
  };

  return (
    <Dialog open={showOptionModal} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h6">Manage Groups</Typography>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {options.map((opt, index) => (
            <Box key={index} display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <TextField
                size="small"
                fullWidth
                label={`Group ${index + 1}`}
                value={opt.title}
                placeholder="Enter Group Title"
                onChange={(e) => handleInputChange(e.target.value, index)}
                sx={{
                  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                    top: '2px',
                  },
                }}
              />
              <TextField
                select
                size="small"
                fullWidth
                SelectProps={{ multiple: true }}
                label="Fields in group"
                value={opt.fieldKeys}
                onChange={(e) => {
                  const raw = e.target.value;
                  handleFieldChange(Array.isArray(raw) ? raw : [raw], index);
                }}
                sx={{
                  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                    top: '2px',
                  },
                }}
              >
                {(fields || []).map((field) => {
                  const fieldValue = field?.item_id ?? field?.key_name ?? field?.name ?? '';
                  const fieldLabel = field?.alias_name || field?.key_name || field?.name || fieldValue;
                  return (
                    <MenuItem key={fieldValue} value={fieldValue}>
                      {fieldLabel}
                    </MenuItem>
                  );
                })}
              </TextField>
              <IconButton aria-label="remove" onClick={() => handleRemoveOption(index)}>
                <CloseIcon />
              </IconButton>
              {index === options.length - 1 && (
                <IconButton aria-label="add" onClick={addOptionRow}>
                  <AddIcon />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" size="small" onClick={handleSubmit}>
          Submit
        </Button>
        <Button onClick={onCancel} size="small">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupModal;
